import { extractTenantId } from "@/helpers/tenantHelper";

type DoctorSummary = Record<string, unknown> & {
  userId?: string | number | null;
};

type DoctorEntry = Record<string, unknown> & {
  id?: string | number | null;
  rawSummary?: DoctorSummary | null;
  resourceId?: string | number | null;
  tenantId?: string | number | null;
  tenantXId?: string | number | null;
  classes?: Array<Record<string, unknown>> | null;
  bookingOptions?: Array<Record<string, unknown>> | null;
  centers?: Array<Record<string, unknown>> | null;
  totalDatesMax?: number | null;
  totalTimesMax?: number | null;
  scheduleVersion?: number | null;
  baseDateIso?: string | null;
};

type ClassesByTherapist = Map<string, Array<Record<string, unknown>>>;

export function buildClassMapKey(
  tenantKey: string | number | null | undefined,
  therapistId: string | number | null | undefined,
) {
  if (tenantKey === null || tenantKey === undefined) {
    return null;
  }
  if (therapistId === null || therapistId === undefined) {
    return null;
  }
  const tenantValue =
    extractTenantId(String(tenantKey)) || String(tenantKey).trim();
  const therapistValue = String(therapistId).trim();
  if (!tenantValue || !therapistValue) {
    return null;
  }
  return `${tenantValue}:${therapistValue}`;
}

export function mergeClassesIntoDoctors(
  doctors: DoctorEntry[],
  classesByTherapist: ClassesByTherapist,
) {
  doctors.forEach((doctor) => {
    if (!doctor) {
      return;
    }
    const doctorId =
      doctor.id !== undefined && doctor.id !== null
        ? doctor.id
        : doctor.rawSummary &&
          doctor.rawSummary.userId !== undefined &&
          doctor.rawSummary.userId !== null
        ? doctor.rawSummary.userId
        : doctor.resourceId;
    const tenantCandidates = [];
    if (doctor.tenantId) {
      tenantCandidates.push(doctor.tenantId);
    }
    if (doctor.tenantXId && doctor.tenantXId !== doctor.tenantId) {
      tenantCandidates.push(doctor.tenantXId);
    }
    const classKeys = tenantCandidates
      .map((tenant) => buildClassMapKey(tenant, doctorId))
      .filter((value): value is string => Boolean(value));
    if (!classKeys.length) {
      doctor.classes = [];
      return;
    }
    let classes = null;
    for (const classKey of classKeys) {
      const candidate = classesByTherapist.get(classKey);
      if (candidate) {
        classes = candidate;
        break;
      }
    }
    doctor.classes = Array.isArray(classes) ? classes : [];
  });
}

export function reuseExistingDoctorState(
  nextDoctors: DoctorEntry[],
  previousDoctors: DoctorEntry[],
  options: { reuseSchedule?: boolean } = {},
) {
  if (
    !Array.isArray(nextDoctors) ||
    !Array.isArray(previousDoctors) ||
    !previousDoctors.length
  ) {
    return;
  }

  const reuseSchedule = options.reuseSchedule !== false;

  nextDoctors.forEach((doctor) => {
    const reference = previousDoctors.find((entry) =>
      isSameDoctorEntry(entry, doctor),
    );
    if (!reference) {
      return;
    }

    const previousOptions = Array.isArray(reference.bookingOptions)
      ? reference.bookingOptions
      : [];
    const nextOptions = Array.isArray(doctor.bookingOptions)
      ? doctor.bookingOptions
      : [];
    const mergedOptions = nextOptions.map((option) => {
      const matched = previousOptions.find((entry) => {
        if (!entry || !option) {
          return false;
        }
        if (entry.key === undefined || entry.key === null) {
          return false;
        }
        if (option.key === undefined || option.key === null) {
          return false;
        }
        return String(entry.key) === String(option.key);
      });

      if (!matched) {
        return option;
      }

      const nextSchedule = Array.isArray(option.schedule)
        ? option.schedule
        : [];
      const nextHours = Array.isArray(option.hours) ? option.hours : [];
      const schedule = reuseSchedule
        ? nextSchedule.length
          ? nextSchedule
          : Array.isArray(matched.schedule)
          ? matched.schedule
          : []
        : nextSchedule;
      const hours = reuseSchedule
        ? nextHours.length
          ? nextHours
          : Array.isArray(matched.hours)
          ? matched.hours
          : []
        : nextHours;
      const price = option.price ?? matched.price ?? null;
      const priceLocalized =
        option.priceLocalized ?? matched.priceLocalized ?? null;
      const scheduleLoaded = reuseSchedule
        ? option.scheduleLoaded || matched.scheduleLoaded || schedule.length > 0
        : schedule.length > 0;
      const scheduleVersion = reuseSchedule
        ? matched.scheduleVersion ?? option.scheduleVersion ?? 0
        : option.scheduleVersion ?? 0;

      return {
        ...option,
        schedule,
        hours,
        price,
        priceLocalized,
        scheduleLoaded,
        scheduleLoading: false,
        scheduleError: null,
        scheduleVersion,
      };
    });

    doctor.bookingOptions = mergedOptions;
    doctor.totalDatesMax = reuseSchedule
      ? doctor.totalDatesMax ?? reference.totalDatesMax ?? 0
      : doctor.totalDatesMax ?? 0;
    doctor.totalTimesMax = reuseSchedule
      ? doctor.totalTimesMax ?? reference.totalTimesMax ?? 0
      : doctor.totalTimesMax ?? 0;
    doctor.scheduleVersion = reuseSchedule
      ? doctor.scheduleVersion ?? reference.scheduleVersion ?? 0
      : doctor.scheduleVersion ?? 0;
    doctor.baseDateIso = doctor.baseDateIso ?? reference.baseDateIso ?? null;

    if (Array.isArray(doctor.centers) && doctor.centers.length) {
      const previousCenters = Array.isArray(reference.centers)
        ? reference.centers
        : [];
      doctor.centers = doctor.centers.map((center) => {
        if (!center || center.key === undefined || center.key === null) {
          return center;
        }
        const matchedCenter = previousCenters.find(
          (entry) =>
            entry &&
            entry.key !== undefined &&
            entry.key !== null &&
            String(entry.key) === String(center.key),
        );
        if (!matchedCenter) {
          return center;
        }
        const previousVariants = Array.isArray(matchedCenter.variantKeys)
          ? matchedCenter.variantKeys
          : [];
        const nextVariants = Array.isArray(center.variantKeys)
          ? center.variantKeys
          : [];
        const mergedVariants = Array.from(
          new Set([...nextVariants, ...previousVariants]),
        );
        return {
          ...center,
          variantKeys: mergedVariants,
        };
      });
    }
  });
}

export function isSameDoctorEntry(source: DoctorEntry, reference: DoctorEntry) {
  if (!source || !reference) {
    return false;
  }

  const normalize = (value: unknown) =>
    value === null || value === undefined ? null : String(value);

  const sourceKey = normalize(source.entryKey);
  const referenceKey = normalize(reference.entryKey);
  if (sourceKey && referenceKey) {
    return sourceKey === referenceKey;
  }

  const sourceId = normalize(source.id);
  const referenceId = normalize(reference.id);
  if (sourceId && referenceId && sourceId !== referenceId) {
    return false;
  }

  const sourceTenant = normalize(source.tenantXId ?? source.tenantId);
  const referenceTenant = normalize(reference.tenantXId ?? reference.tenantId);

  if (referenceTenant && !sourceTenant) {
    return false;
  }

  if (sourceTenant && !referenceTenant) {
    return false;
  }

  if (referenceTenant && sourceTenant && sourceTenant !== referenceTenant) {
    return false;
  }

  if (sourceId && referenceId) {
    return sourceId === referenceId;
  }

  const sourceResource = normalize(source.resourceId);
  const referenceResource = normalize(reference.resourceId);
  if (sourceTenant && referenceTenant && sourceResource && referenceResource) {
    return (
      sourceTenant === referenceTenant && sourceResource === referenceResource
    );
  }

  return false;
}
